import React, { useState, useEffect, useContext, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import nanoid from 'nanoid';
import { loader } from 'graphql.macro';
import { useQuery, useMutation } from 'react-apollo-hooks';

import { withGraphQLErrors, ProfileContext } from '@loyalup/core';

import {
  Hint,
  WithTitle,
  Button,
  AddressInput,
  Input,
  Select,
  Form,
  Toggle,
  Breadcrumbs,
  useNotifications,
  FileUploader,
  ImageCrop,
  WithFade,
} from '@loyalup/ui';

import Phone from 'components/Phone';
import PhoneForm from 'components/PhoneForm';
import { getClientFields } from 'components/ClientFields';

import onlyData from 'utils/onlyData';
import updateProfileInCache from 'utils/updateProfileInCache';

import { ReactComponent as PlusIcon } from 'icons/plus.svg';
import { ReactComponent as TrashIcon } from 'icons/trash.svg';
import { ReactComponent as XIcon } from 'icons/x.svg';

import s from './PassDesigner.module.css';

const getId = () => nanoid(6);

const PASS_DESIGN_QUERY = loader('../../gql/queries/PassDesign.gql');
const SAVE_PASS_DESIGN_MUTATION = loader('../../gql/mutations/SavePassDesign.gql');
const CREATE_IMAGE_MUTATION = loader('../../gql/mutations/CreateImage.gql');
const SET_IMAGE_VIEWPORT_MUTATION = loader('../../gql/mutations/SetImageViewport.gql');
const SET_CURRENT_PASS_DESIGN_MUTATION = loader('../../gql/mutations/SetCurrentPassDesign.gql');

const IMAGE_TYPE = {
  STRIP: 'stripImage',
  LOGO: 'logoImage',
  ICON: 'iconImage',
};

const IMAGE_SIZE = {
  stripImage: [375 * 2, 144 * 2],
  logoImage: [160 * 2, 50 * 2],
  iconImage: [29 * 2, 29 * 2],
};

const findAndReplace = (data, i, col) => [...col.slice(0, i), { ...col[i], ...data }, ...col.slice(i + 1)];

const PassDesigner = () => {
  const [t] = useTranslation();
  const [sendNotification] = useNotifications();

  const { company } = useContext(ProfileContext);

  const currentPresetName = company.currentPassDesign?.name;

  const [presets, setPresets] = useState(null);

  const [hintPreview, showHintPreview] = useState(false);

  const [cardData, setCardData] = useState({
    name: company.currentPassDesign?.name || 'discount',
    barcodeEnabled: false,
    feedbackEnabled: false,
    barcodeType: 'CODE128',
    barcodeBinding: 'cardNumber',
    barcodeAltMessage: 'Powered by loyal-up.com',
    lastMessage: t('Здесь будет текст последнего полученного Push-уведомления'),
    backgroundColor: '#1f2431',
    labelColor: '#9ca2ae',
    foregroundColor: '#ffffff',
  });

  const [editFrontField, setEditFrontField] = useState(null);
  const [editValueType, setEditValueType] = useState('value');

  useEffect(() => {
    if (editFrontField) {
      const { value, binding } = editFrontField;
      value && !binding && setEditValueType('value');
      binding && !value && setEditValueType('binding');
    }
  }, [editFrontField]);

  const getPassDataFromUpdate = useCallback(data => {
    const transformArrayToObjectFields = fields =>
      fields.reduce((acc, field) => {
        const id = getId();
        return {
          ...acc,
          [id]: {
            ...field,
            id,
          },
        };
      }, {});

    return Object.keys(data).reduce((acc, key) => {
      if (key === 'infoFields') {
        return { ...acc, infoFields: transformArrayToObjectFields(data.infoFields) };
      }
      if (key === 'backFields') {
        return { ...acc, backFields: transformArrayToObjectFields(data.backFields) };
      }
      return { ...acc, [key]: data[key] };
    }, {});
  }, []);

  const getCardDataForUpdate = useCallback(() => {
    const transformInfoField = ({ label, value, type }, index) => ({
      label,
      value,
      type,
      index,
    });

    const transformBackField = ({ label, value }, index) => ({
      label,
      value,
      index,
    });

    const withoutEmptyValues = obj =>
      Object.keys(obj).reduce((acc, key) => {
        if (obj[key] === null) {
          return acc;
        }
        return { ...acc, [key]: obj[key] };
      }, {});

    const updateData = Object.keys(cardData).reduce((acc, key) => {
      switch (key) {
        case 'headerFields':
          return { ...acc, headerFields: cardData.headerFields.map(onlyData) };
        case 'secondaryFields':
          return { ...acc, secondaryFields: cardData.secondaryFields.map(data => onlyData(withoutEmptyValues(data))) };
        case 'lastMessage':
          return acc;
        case 'infoFields':
          return {
            ...acc,
            infoFields: Object.keys(cardData.infoFields).map((infoFieldKey, i) =>
              transformInfoField(cardData?.infoFields[infoFieldKey], i),
            ),
          };
        case 'backFields':
          return {
            ...acc,
            backFields: Object.keys(cardData.backFields).map((backFieldKey, i) =>
              transformBackField(cardData?.backFields[backFieldKey], i),
            ),
          };
        case 'stripImage':
          return { ...acc, stripImageId: cardData.stripImage.id };
        case 'logoImage':
          return { ...acc, logoImageId: cardData.logoImage.id };
        case 'iconImage':
          return { ...acc, iconImageId: cardData.iconImage.id };
        // workaround
        case 'id':
          const nameForId = presets.find(({ id }) => id === cardData.id).name;
          if (nameForId === 'bonuses' || nameForId === 'discount' || nameForId === 'custom') {
            return { ...acc, id: cardData.id };
          }
          return acc;
        default:
          return { ...acc, [key]: cardData[key] };
      }
    }, {});

    return onlyData(updateData);
  }, [cardData, presets]);

  const [showScreen, setShowScreen] = useState('design');

  const [activeImageType, setActiveImageType] = useState();
  const [imageCropping, setImageCropping] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);

  const activeImage = cardData && activeImageType && cardData[activeImageType];
  const activeImageSize = activeImageType && IMAGE_SIZE[activeImageType];

  const uploadRef = useRef();

  const startImageUpload = imageType => {
    setActiveImageType(imageType);
    uploadRef.current.selectFile();
  };

  const cardTypeItems = useMemo(
    () => [
      {
        label: t('Скидка') + ('discount' === currentPresetName ? ` (${t('Карта по умолчанию')})` : ''),
        value: 'discount',
      },
      {
        label: t('Бонусы') + ('bonuses' === currentPresetName ? ` (${t('Карта по умолчанию')})` : ''),
        value: 'bonuses',
      },
      {
        label: t('Другая') + ('custom' === currentPresetName ? ` (${t('Карта по умолчанию')})` : ''),
        value: 'custom',
      },
    ],
    [currentPresetName, t],
  );

  const barcodeBindingFields = useMemo(
    () => [
      { label: t('Номер карты'), value: 'cardNumber' },
      { label: t('Имя пользователя'), value: 'fullname' },
      { label: t('Только имя'), value: 'fistName' },
      { label: t('Только фамилия'), value: 'lastName' },
      { label: t('Е-мейл'), value: 'email' },
      { label: t('Телефон'), value: 'phone' },
      { label: t('Пол'), value: 'gender' },
      { label: t('Город'), value: 'city' },
      { label: t('Скидка'), value: 'discount' },
      { label: t('Бонусы'), value: 'bonuses' },
      { label: t('День Рождения'), value: 'bday' },
    ],
    [t],
  );

  const passFieldTypes = useMemo(
    () => [
      { label: t('Ссылка'), value: 'LINK' },
      { label: t('Гео'), value: 'LOCATION' },
      { label: t('Телефон'), value: 'PHONE' },
      { label: t('Текст'), value: 'TEXT' },
    ],
    [t],
  );

  const { data, refetch } = useQuery(PASS_DESIGN_QUERY, {
    suspend: false,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const [savePassDesign, { loading }] = useMutation(SAVE_PASS_DESIGN_MUTATION);
  const [createImage] = useMutation(CREATE_IMAGE_MUTATION);
  const [setImageViewport] = useMutation(SET_IMAGE_VIEWPORT_MUTATION);
  const [setCurrentPassDesign, { loading: setCurrentPassDesignLoading }] = useMutation(
    SET_CURRENT_PASS_DESIGN_MUTATION,
  );

  const makeDefaultCard = async name => {
    try {
      await setCurrentPassDesign({
        variables: { name },
        update: (cache, { data: { setCurrentPassDesign } }) => {
          updateProfileInCache(cache, {
            company: { ...company, currentPassDesign: setCurrentPassDesign },
          });
        },
      });
    } catch (e) {
      // console.log(e)
    }
  };

  const onSubmitUpdatePassDesign = async () => {
    const { errors } = await withGraphQLErrors(() =>
      savePassDesign({
        variables: { design: getCardDataForUpdate() },
        update: () => {
          setShowScreen('design');
          window.scrollTo(0, 0);
          sendNotification({ text: t('Дизайн карты сохранён') });
        },
      }),
    );
    if (errors) {
      sendNotification({ type: 'error', text: t('Ошибка при сохранении дизайна') });
      return;
    }
    if (!currentPresetName) {
      try {
        await makeDefaultCard(cardData.name);
        sendNotification({ text: t('Теперь это дизайн по умолчанию') });
      } catch (e) {
        // console.log(e)
      }
    }
    refetch();
  };

  const overrideHeaderData = useMemo(
    () => ({
      bonuses: {
        label: t('Бонусы'),
        binding: 'bonuses',
      },
      discount: {
        label: t('Скидки'),
        binding: 'discount',
      },
      custom: {
        label: t('Другая'),
        binding: 'cardNumber',
      },
    }),
    [t],
  );

  useEffect(() => {
    if (data?.passDesigns) {
      setPresets(data?.passDesigns.map(getPassDataFromUpdate));
    }
  }, [data, getPassDataFromUpdate]);

  useEffect(() => {
    if (Array.isArray(presets) && presets.length) {
      const existingPreset = presets.find(({ name }) => name === cardData.name);
      if (existingPreset) {
        setCardData(prevData => ({ ...prevData, ...existingPreset }));
      } else {
        setCardData(prevData => ({
          ...prevData,
          ...presets[0],
          name: prevData.name,
          headerFields: [
            {
              ...presets[0].headerFields[0],
              ...overrideHeaderData[cardData.name],
            },
          ],
        }));
      }
    }
    setEditFrontField(null);
  }, [cardData.name, overrideHeaderData, presets]);

  const cardName = useMemo(() => cardData.name, [cardData.name]);

  const createdPreset = useMemo(() => presets?.find(({ name }) => name === cardName), [cardName, presets]);

  useEffect(() => {
    if (createdPreset) {
      setCardData(prevData => ({ ...prevData, ...createdPreset }));
    }
  }, [createdPreset]);

  const addInfoField = () =>
    setCardData(prevData => {
      const id = getId();
      return prevData?.infoFields
        ? { ...prevData, infoFields: { ...prevData.infoFields, [id]: { id, type: 'LINK' } } }
        : { ...prevData, infoFields: { [id]: { id, type: 'LINK' } } };
    });

  const onChangeInfoField = (infoField, field, value) =>
    setCardData(prevData => ({
      ...prevData,
      infoFields: { ...prevData.infoFields, [infoField.id]: { ...infoField, [field]: value } },
    }));

  const onDeleteInfoField = ({ id }) => {
    const infoFields = Object.keys(cardData?.infoFields).reduce(
      (acc, key) => (key === id ? acc : { ...acc, [key]: cardData?.infoFields[key] }),
      {},
    );
    setCardData(prevData => ({ ...prevData, infoFields }));
  };

  const onAddBackFeild = () => {
    setCardData(prevData => {
      const id = getId();
      return prevData?.backFields
        ? { ...prevData, backFields: { ...prevData.backFields, [id]: { id } } }
        : { ...prevData, backFields: { [id]: { id } } };
    });
  };

  const onChangeBackFieldData = (key, value, field) => {
    setCardData(prevData => ({
      ...prevData,
      backFields: { ...prevData.backFields, [field.id]: { ...field, [key]: value } },
    }));
  };

  const onRemoveBackFieldData = ({ id }) => {
    const backFields = Object.keys(cardData?.backFields).reduce(
      (acc, key) => (key === id ? acc : { ...acc, [key]: cardData?.backFields[key] }),
      {},
    );
    setCardData(prevData => ({
      ...prevData,
      backFields,
    }));
  };

  const canBeDefault = useMemo(
    () =>
      !!currentPresetName && cardData?.name !== currentPresetName && presets.find(({ name }) => name === cardData.name),
    [cardData, currentPresetName, presets],
  );

  const renderDesign = sectionClassName => (
    <Form noContainers className={s.settings} onSubmit={() => setShowScreen('info')}>
      <section className={sectionClassName}>
        <h2 className={s.sectionTitle}>{t('Тип карты')}</h2>
        <div className={s.selectedDesign}>
          <Select
            className={s.field}
            fixedWidth={260}
            stretch
            value={cardData?.name}
            theme="toolbar-white"
            onChange={name => setCardData(prevData => ({ ...prevData, name }))}
            items={cardTypeItems}
          />
          {canBeDefault && (
            <Button
              className={s.defaultDesignButton}
              loading={setCurrentPassDesignLoading}
              onClick={async () => {
                try {
                  await makeDefaultCard(cardData.name);
                  sendNotification({ text: t('Теперь это дизайн по умолчанию') });
                  refetch();
                } catch (e) {
                  // console.log(e)
                }
              }}
              theme="green"
            >
              {t('Сделать по умолчанию')}
            </Button>
          )}
        </div>
      </section>
      <WithFade component="div" className={sectionClassName}>
        {!!editFrontField && renderFrontFieldEdit(sectionClassName)}
      </WithFade>
      <section className={sectionClassName}>
        <h2 className={s.sectionTitle}>{t('Дизайн карты')}</h2>
        <div className={s.colors}>
          <label className={cn(s.inputWithLabel, s.subtitle)}>
            {t('Цвет заголовка')}
            <Input
              required
              fixedWidth={140}
              className={s.input}
              theme="toolbar-white"
              value={cardData?.labelColor}
              onChange={labelColor => setCardData(prevData => ({ ...prevData, labelColor }))}
              type="color-picker"
            />
          </label>
          <label className={cn(s.inputWithLabel, s.subtitle)}>
            {t('Цвет текста')}
            <Input
              required
              fixedWidth={140}
              className={s.input}
              theme="toolbar-white"
              value={cardData?.foregroundColor}
              onChange={foregroundColor => setCardData(prevData => ({ ...prevData, foregroundColor }))}
              type="color-picker"
            />
          </label>
          <label className={cn(s.inputWithLabel, s.subtitle)}>
            {t('Цвет фона')}
            <Input
              required
              fixedWidth={140}
              className={s.input}
              theme="toolbar-white"
              value={cardData?.backgroundColor}
              onChange={backgroundColor => setCardData(prevData => ({ ...prevData, backgroundColor }))}
              type="color-picker"
            />
          </label>
        </div>
      </section>
      <section className={sectionClassName}>
        <h2 className={s.sectionTitle}>{t('Уведомления и подсказки')}</h2>

        <div className={s.hintsContainer}>
          <div className={s.hintSetting}>
            <span className={s.label}>{t('Иконка Push-уведомления')}</span>
            <div className={s.value}>
              <img alt="" src={cardData?.iconImage?.url} className={s.iconImage} />
              <Button
                loading={imageUploading && activeImageType === IMAGE_TYPE.ICON}
                onClick={() => startImageUpload(IMAGE_TYPE.ICON)}
                theme="text"
                className={s.button}
                center
              >
                {t('Изменить')}
              </Button>
            </div>
          </div>
          <div className={s.hintSetting}>
            <span className={s.label}>
              {t('Отображать пуш-уведомление на карте')}
              <Hint text={t('Каждое сообщение содержит в нижней части информацию о способе переворота карты')} />
            </span>
            <div className={s.value}>
              <Select
                required={!!cardData?.bannerPeriod}
                value={cardData?.bannerEnabled === false ? false : cardData?.bannerPeriod}
                onChange={value => {
                  const bannerEnabled = typeof value === 'number';
                  const bannerPeriod = +value;
                  setCardData(prevData => ({ ...prevData, bannerEnabled, bannerPeriod }));
                }}
                className={s.select}
                fixedWidth={200}
                stretch
                items={[
                  {
                    label: t('Не показывать'),
                    value: false,
                  },
                  {
                    label: t('10 минут'),
                    value: 10,
                  },
                  {
                    label: t('30 минут'),
                    value: 30,
                  },
                  {
                    label: t('1 час'),
                    value: 60,
                  },
                  {
                    label: t('4 часа'),
                    value: 240,
                  },
                  {
                    label: t('8 часов'),
                    value: 480,
                  },
                  {
                    label: t('День'),
                    value: 1440,
                  },
                  {
                    label: t('Неделю'),
                    value: 10080,
                  },
                  {
                    label: t('Всегда'),
                    value: 0,
                  },
                ]}
                theme="toolbar-white"
              />
              <Button
                disabled={!cardData?.bannerEnabled}
                className={s.button}
                onClick={() => showHintPreview(!hintPreview)}
                theme="text"
              >
                {t(hintPreview ? 'Скрыть пример' : 'Показать пример')}
              </Button>
            </div>
          </div>
          <div className={s.hintSetting}>
            <span className={s.label}>
              {t('Подсказка о перевороте карты')}
              <Hint text={t('Отображается на карте, когда нет активного пуш-уведомления')} />
            </span>
            <div className={s.value}>
              <Select
                required={cardData?.hintEnabled}
                value={cardData?.hintEnabled}
                onChange={hintEnabled => {
                  setCardData(data => ({ ...data, hintEnabled }));
                }}
                className={s.select}
                fixedWidth={200}
                stretch
                items={[
                  { label: t('Показывать'), value: true },
                  {
                    label: t('Не показывать'),
                    value: false,
                  },
                ]}
                theme="toolbar-white"
              />
            </div>
          </div>
        </div>
      </section>
      <section className={sectionClassName}>
        <h2 className={s.sectionTitle}>{t('Настройки штрихкода')}</h2>
        <div className={s.barcodeSettingsRow}>
          <label className={cn(s.selectWithLabel, s.subtitle)}>
            <span className={s.labelWithHint}>
              {t('Формат штрихкода')}
              <Hint
                text={t(
                  'Формат штрихкода зависит от оборудования, которым вы будете считывать карты. Если вы не уверены, какой выбрать, оставьте тип по умолчанию.',
                )}
              />
            </span>
            <Select
              fixedWidth={200}
              stretch
              value={cardData?.barcodeEnabled === false ? false : cardData?.barcodeType}
              items={[
                {
                  label: t('Не показывать'),
                  value: false,
                },
                {
                  label: t('Code 128'),
                  value: 'CODE128',
                },
                {
                  label: 'AZTEC',
                  value: 'AZTEC',
                },
                {
                  label: 'PDF417',
                  value: 'PDF417',
                },
                {
                  label: 'QR',
                  value: 'QR',
                },
              ]}
              onChange={value => {
                const barcodeEnabled = value !== false;
                const barcodeType = value === false ? cardData?.barcodeType : value;
                setCardData(prevData => ({ ...prevData, barcodeEnabled, barcodeType }));
              }}
              className={s.select}
              theme="toolbar-white"
            />
          </label>
          <label className={cn(s.selectWithLabel, s.subtitle)}>
            <span className={s.labelWithHint}>
              {t('Поле для кодирования')}
              <Hint
                text={t(
                  'Если вы планируете использовать сканер штрихкодов, рекомендуем использовать Номер карты. Если считывание будет через камеру телефона, можете выбрать любое поле.',
                )}
              />
            </span>
            <Select
              fixedWidth={200}
              stretch
              value={cardData?.barcodeBinding}
              onChange={barcodeBinding => setCardData(prevData => ({ ...prevData, barcodeBinding }))}
              items={barcodeBindingFields}
              className={s.select}
              theme="toolbar-white"
            />
          </label>
        </div>
        <label className={cn(s.inputWithLabel, s.subtitle)}>
          <span className={s.labelWithHint}>{t('Подпись под кодом')}</span>
          <Input
            required={cardData?.barcodeEnabled}
            value={cardData?.barcodeAltMessage || ''}
            onChange={value => {
              const barcodeAltMessage = value.slice(0, 60);
              setCardData(prevData => ({ ...prevData, barcodeAltMessage }));
            }}
            maxLength={35}
            fixedWidth={425}
            className={s.input}
            theme="toolbar-white"
          />
        </label>
      </section>
      <section className={s.buttons}>
        <div className={s.sectionContent}>
          <Button type="submit" fixedWidth={200} center>
            {t('Далее')}
          </Button>
        </div>
      </section>
    </Form>
  );

  const renderInfoField = infoField => (
    <div key={infoField.id} className={s.infoFieldRow}>
      {infoField.type !== 'LOCATION' && (
        <Input
          required
          value={infoField.label}
          onChange={text => onChangeInfoField(infoField, 'label', text)}
          className={s.infoFieldInput}
          theme="toolbar-white"
          placeholder={t('Текст')}
          autoFocus
        />
      )}
      {infoField.type === 'LOCATION' ? (
        <AddressInput
          selectedItem={{ label: infoField.label }}
          onChange={(_, { label, placeId }) => {
            onChangeInfoField(infoField, 'value', placeId);
            onChangeInfoField(infoField, 'label', label);
          }}
          theme="toolbar-white"
          className={s.infoFieldInput}
          placeholder={t('Адрес')}
          fixedWidth={360}
        />
      ) : (
        <Input
          required
          value={infoField.value}
          onChange={value => onChangeInfoField(infoField, 'value', value)}
          className={s.infoFieldInput}
          theme="toolbar-white"
          placeholder={t('Значение')}
        />
      )}
      <Select
        fixedWidth={122}
        stretch
        className={s.infoFieldInput}
        theme="toolbar-white"
        items={passFieldTypes}
        value={infoField.type}
        onChange={value => onChangeInfoField(infoField, 'type', value)}
      />
      <TrashIcon width={13} height={14} onClick={() => onDeleteInfoField(infoField)} className={s.deleteIcon} />
    </div>
  );

  const renderInfo = sectionClassName => (
    <Form
      noContainers
      className={s.settings}
      onSubmit={() => {
        onSubmitUpdatePassDesign();
      }}
    >
      <section className={sectionClassName}>
        <h2 className={s.sectionTitle}>{t('Информация')}</h2>
        <p className={cn(s.subtitle, s.infoTitle)}>{t('Последнее сообщение')}</p>
        <Input
          required
          className={s.input}
          value={cardData?.lastMessage}
          onChange={() => null}
          type="textarea"
          theme="toolbar-white"
          disabled
        />
        <p className={cn(s.subtitle, s.infoTitle)}>{t('Возможности')}</p>
        <WithFade>
          {cardData?.infoFields &&
            Object.keys(cardData?.infoFields).map(id => renderInfoField(cardData?.infoFields[id]))}
        </WithFade>
        <Button onClick={addInfoField} center icons="only" theme="green">
          <PlusIcon />
        </Button>
        <div className={s.line} />
        <Toggle
          checked={cardData?.feedbackEnabled}
          onChange={feedbackEnabled => setCardData(prevData => ({ ...prevData, feedbackEnabled }))}
          className={s.toggle}
          labelClassName={s.toggleLabel}
          label={<span className={s.subtitle}>{t('Обратная связь')}</span>}
        />
        <WithFade>
          {cardData?.feedbackEnabled && (
            <div>
              <Input
                required={cardData?.feedbackEnabled}
                placeholder={t('Свяжитесь с нами')}
                className={cn(s.input, s.contactUsInput)}
                value={cardData?.feedbackLabel}
                onChange={feedbackLabel => setCardData(prevData => ({ ...prevData, feedbackLabel }))}
                type="text"
                theme="toolbar-white"
                autoFocus
              />
            </div>
          )}
        </WithFade>
        <WithFade>
          {Object.keys(cardData?.backFields || {}).length && <div className={s.line} />}
          {cardData?.backFields &&
            Object.keys(cardData?.backFields).map(id => (
              <div key={id} className={s.backField}>
                <div className={s.backFieldInputs}>
                  <Input
                    required
                    placeholder={t('Введите заголовок')}
                    className={s.input}
                    value={cardData?.backFields[id].label}
                    onChange={value => onChangeBackFieldData('label', value, cardData?.backFields[id])}
                    type="text"
                    theme="toolbar-white"
                    autoFocus
                  />
                  <Input
                    required
                    placeholder={t('Введите текст')}
                    className={s.input}
                    value={cardData?.backFields[id].value}
                    onChange={value => onChangeBackFieldData('value', value, cardData?.backFields[id])}
                    maxLength={null}
                    type="textarea"
                    wrap="hard"
                    theme="toolbar-white"
                  />
                </div>
                <TrashIcon
                  width={13}
                  height={14}
                  onClick={() => onRemoveBackFieldData(cardData?.backFields[id])}
                  className={s.deleteIcon}
                />
              </div>
            ))}
        </WithFade>
        <div className={s.line} />
        <Button icons="first" theme="green" onClick={onAddBackFeild} center>
          <PlusIcon />
          {t('Добавить текстовое поле')}
        </Button>
      </section>
      <section className={s.buttons}>
        <Button theme="black" onClick={() => setShowScreen('design')} center>
          {t('Назад')}
        </Button>
        <Button loading={loading} theme="green" type="submit" fixedWidth={200} center>
          {t('Сохранить')}
        </Button>
      </section>
    </Form>
  );

  const renderFrontFieldEdit = sectionClassName => {
    const type = editFrontField?.type;

    const shutOppositeFieldFor = name => {
      if (name === 'binding') return { value: '' };
      if (name === 'value') return { binding: '' };
      return {};
    };

    const onChange = (name, value) => {
      setEditFrontField(prev => ({ ...prev, [name]: value, ...shutOppositeFieldFor(name) }));
      setCardData(prevData => ({
        ...prevData,
        [type]: findAndReplace({ [name]: value, ...shutOppositeFieldFor(name) }, editFrontField?.index, prevData[type]),
      }));
    };

    return (
      <div className={s.frontFieldEditForm} onMouseDown={e => e.stopPropagation()}>
        <h2 className={cn(s.sectionTitle, s.titleRow)}>
          {t('Редактирование поля')} «{editFrontField?.label}»
          <div className={s.hideSection}>
            <XIcon onClick={() => setEditFrontField(null)} />
          </div>
        </h2>
        <div className={s.editFrontFieldInput}>
          <label className={cn(s.inputWithLabel, s.subtitle)}>
            {t('Заголовок')}
            <Input
              className={s.input}
              value={editFrontField?.label || ''}
              onChange={label => onChange('label', label)}
              type="text"
              theme="toolbar-white"
              maxLength={14}
              autoFocus
            />
          </label>
        </div>

        <div className={s.fieldFormRow}>
          <label className={cn(s.selectWithLabel, s.subtitle)}>
            <span className={s.labelWithHint}>
              {t('Тип поля')}
              <Hint text={t('Вы можете добавить текст или подставить динамическое значение для конкретного клиента')} />
            </span>
            <Select
              value={editValueType}
              items={[
                { label: t('Текст'), value: 'value' },
                { label: t('Значение'), value: 'binding' },
              ]}
              theme="toolbar-white"
              className={s.select}
              onChange={valueType => setEditValueType(valueType)}
              fixedWidth={200}
              stretch
            />
          </label>

          <label className={cn(s.inputWithLabel, s.selectWithLabel, s.subtitle)}>
            <span className={s.labelWithHint}>{t('Значение поля')}</span>
            {editValueType === 'binding' ? (
              <Select
                items={[
                  ...getClientFields({ asList: true }).map(({ label, name }) => ({ label, value: name })),
                  { label: t('Имя пользователя'), value: 'fullname' },
                ]}
                theme="toolbar-white"
                value={editFrontField?.binding}
                className={s.select}
                onChange={binding => onChange('binding', binding)}
                fixedWidth={250}
                stretch
              />
            ) : (
              <Input
                className={s.input}
                value={editFrontField?.value || ''}
                onChange={value => onChange('value', value)}
                type="text"
                theme="toolbar-white"
                fixedWidth={250}
              />
            )}
          </label>
        </div>
      </div>
    );
  };

  // i.e. not merged with real data
  if (!cardData.headerFields) return null;

  const dispatchRender = className => {
    if (showScreen === 'design') return renderDesign(className);
    return renderInfo(className);
  };

  return (
    <WithTitle title={t('Дизайнер карт')}>
      <div className={s.header}>
        <h1>{t('Дизайнер карт')}</h1>
        <Breadcrumbs
          withArrows
          className={s.breadcrumbs}
          items={[
            {
              label: t('Дизайн'),
              current: showScreen === 'design',
              disabled: false,
              onSelect: () => setShowScreen('design'),
            },
            {
              label: t('Информация'),
              current: showScreen === 'info',
              disabled: false,
              onSelect: () => setShowScreen('info'),
            },
          ]}
        />
      </div>
      <main onMouseDown={() => (editFrontField ? setEditFrontField(null) : null)} className={s.main}>
        <PhoneForm
          renderPhone={() => (
            <Phone
              type="designer"
              setEditFrontField={setEditFrontField}
              editFrontField={editFrontField}
              onSetBackgroundImage={() => startImageUpload(IMAGE_TYPE.STRIP)}
              onSetLogoImage={() => startImageUpload(IMAGE_TYPE.LOGO)}
              cardData={{
                ...cardData,
                imageLoading: imageUploading && activeImageType === IMAGE_TYPE.STRIP,
                logoLoading: imageUploading && activeImageType === IMAGE_TYPE.LOGO,
              }}
              hintPreview={hintPreview}
              cardSide={showScreen === 'design' ? 'front' : 'back'}
            />
          )}
        >
          {dispatchRender}
        </PhoneForm>
      </main>
      <FileUploader
        hidden
        accept="image/*"
        type="pass_images"
        server={process.env.REACT_APP_SERVER_URL}
        onStart={() => setImageUploading(true)}
        onFinish={async ({ path }) => {
          const [width, height] = activeImageSize;
          const { data } = await createImage({ variables: { path, width, height } });
          setCardData(prevData => ({
            ...prevData,
            [activeImageType]: onlyData(data.createImage),
          }));

          setImageCropping(true);
          setImageUploading(false);
        }}
        ref={uploadRef}
      />
      {activeImage && (
        <ImageCrop
          image={activeImage.rawUrl}
          isOpen={imageCropping}
          onFinish={async viewport => {
            const [width, height] = activeImageSize;
            setImageCropping(false);
            if (viewport) {
              const { data } = await setImageViewport({
                variables: { imageId: activeImage.id, viewport, width, height },
              });
              setCardData(prevData => ({
                ...prevData,
                [activeImageType]: onlyData(data.setImageViewport),
              }));
            }
            setActiveImageType(false);
          }}
          aspectRatio={activeImageSize[0] / activeImageSize[1]}
        />
      )}
    </WithTitle>
  );
};

export default PassDesigner;
